import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Info } from 'lucide-react';

export const LCADL_SECTIONS = {
  selfCare: {
    title: 'Cuidados pessoais',
    activities: ['Secar-se', 'Vestir a parte de cima do corpo', 'Calçar sapatos ou meias', 'Lavar os cabelos'],
  },
  domestic: {
    title: 'Atividades domésticas',
    activities: ['Fazer a cama', 'Trocar os lençóis da cama', 'Lavar janelas/cortinas', 'Tirar o pó', 'Lavar ou secar louça', 'Varrer, passar pano ou aspirar'],
  },
  physical: {
    title: 'Atividade física',
    activities: ['Subir escadas', 'Inclinar-se'],
  },
  leisure: {
    title: 'Lazer',
    activities: ['Caminhar dentro de casa', 'Sair socialmente', 'Falar'],
  },
} as const;

export const LCADL_OPTIONS = [
  { value: 0, label: 'Eu não faria essa atividade de qualquer forma / ela não se aplica' },
  { value: 1, label: 'Não sinto falta de ar ao realizar essa atividade' },
  { value: 2, label: 'Sinto falta de ar moderada ao realizar essa atividade' },
  { value: 3, label: 'Sinto muita falta de ar ao realizar essa atividade' },
  { value: 4, label: 'Não consigo mais realizar essa atividade por causa da falta de ar' },
  { value: 5, label: 'Outra pessoa faz ou me ajuda por causa da falta de ar' },
] as const;

export type LCADLSectionKey = keyof typeof LCADL_SECTIONS;

export interface LCADLSectionPayload {
  activities: string[];
  answers: number[];
  score: number;
  maxScore: number;
}

export interface LCADLResultPayload {
  answers: number[];
  selfCare: LCADLSectionPayload;
  domestic: LCADLSectionPayload;
  physical: LCADLSectionPayload;
  leisure: LCADLSectionPayload;
  totalScore: number;
  maxScore: number;
  percentage: number;
  applicableItemsCount?: number;
  denominatorPoints?: number;
  rawMaximumScore?: number;
  instrumentVersion?: 'LCADL_BR_15_CARPES_2008';
  scoringVersion?: 'LCADL_PERCENT_APPLICABLE_ITEMS_V1';
  finishedAt?: string;
}

interface LCADLTestProps {
  initial?: LCADLResultPayload | null;
  onCancel?: () => void;
  onNext: (result: LCADLResultPayload) => void;
}

const LCADL_TABS = [
  {
    key: 'selfCare',
    title: 'Cuidados pessoais',
    description: 'Aplique primeiro as atividades de autocuidado.',
    sections: ['selfCare'] as const,
  },
  {
    key: 'domestic',
    title: 'Atividades domésticas',
    description: 'Depois registre as tarefas domésticas do dia a dia.',
    sections: ['domestic'] as const,
  },
  {
    key: 'physicalLeisure',
    title: 'Atividade física e lazer',
    description: 'Finalize com mobilidade, participação social e comunicação.',
    sections: ['physical', 'leisure'] as const,
  },
] as const;

export const calculateLCADLScore = (answers: readonly (number | null)[]) => {
  const normalized = answers.map((value) => typeof value === 'number' && value >= 0 && value <= 5 ? value : 0);
  const totalScore = normalized.reduce((sum, value) => sum + value, 0);
  const applicableItemsCount = normalized.filter((value) => value > 0).length;
  const denominatorPoints = applicableItemsCount * 5;
  return {
    totalScore,
    applicableItemsCount,
    denominatorPoints,
    percentage: denominatorPoints > 0
      ? Number(((totalScore / denominatorPoints) * 100).toFixed(1))
      : 0,
  };
};

export default function LCADLTest({ initial, onCancel, onNext }: LCADLTestProps) {
  const totalActivities = Object.values(LCADL_SECTIONS).reduce((sum, section) => sum + section.activities.length, 0);
  const [answers, setAnswers] = useState<(number | null)[]>(Array(totalActivities).fill(null));
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!initial) return;
    if (initial.answers.length === totalActivities) {
      setAnswers(Array.from({ length: totalActivities }, (_, index) => initial.answers[index] ?? null));
    } else {
      const legacyAnswersByActivity = new Map<string, number>();
      (Object.keys(LCADL_SECTIONS) as LCADLSectionKey[]).forEach((sectionKey) => {
        const legacySection = initial[sectionKey];
        legacySection?.activities.forEach((activity, index) => {
          const answer = legacySection.answers[index];
          if (typeof answer === 'number') legacyAnswersByActivity.set(activity, answer);
        });
      });
      setAnswers(
        (Object.values(LCADL_SECTIONS).flatMap((section) => section.activities) as string[])
          .map((activity) => legacyAnswersByActivity.get(activity) ?? null),
      );
    }
    setActiveTab(0);
  }, [initial, totalActivities]);

  const setAnswer = (index: number, value: number) => {
    setAnswers((previous) => {
      const next = [...previous];
      next[index] = value;
      return next;
    });
  };

  const allAnswered = answers.every((answer) => answer !== null);
  const scoreSummary = calculateLCADLScore(answers);
  const totalScore = scoreSummary.totalScore;
  const applicableItemsCount = scoreSummary.applicableItemsCount;
  const maxScore = scoreSummary.denominatorPoints;
  const percentage = scoreSummary.percentage;

  const indexedSections = useMemo(() => {
    let startIndex = 0;
    return (Object.entries(LCADL_SECTIONS) as Array<[LCADLSectionKey, (typeof LCADL_SECTIONS)[LCADLSectionKey]]>).map(([key, section]) => {
      const activities = section.activities.map((activity, offset) => ({
        activity,
        index: startIndex + offset,
      }));
      startIndex += section.activities.length;
      return {
        key,
        title: section.title,
        activities,
      };
    });
  }, []);

  const sectionsByKey = useMemo(
    () =>
      indexedSections.reduce<Record<LCADLSectionKey, (typeof indexedSections)[number]>>((accumulator, section) => {
        accumulator[section.key] = section;
        return accumulator;
      }, {} as Record<LCADLSectionKey, (typeof indexedSections)[number]>),
    [indexedSections],
  );

  const tabProgress = LCADL_TABS.map((tab) => {
    const indices = tab.sections.flatMap((sectionKey) => sectionsByKey[sectionKey].activities.map((activity) => activity.index));
    const answeredCount = indices.filter((index) => answers[index] !== null).length;
    return {
      total: indices.length,
      answered: answeredCount,
      complete: answeredCount === indices.length,
    };
  });

  const currentTab = LCADL_TABS[activeTab];
  const currentSections = currentTab.sections.map((sectionKey) => sectionsByKey[sectionKey]);
  const canAdvanceFromCurrentTab = tabProgress[activeTab].complete;

  const handleFinish = () => {
    let index = 0;
    const result: Partial<LCADLResultPayload> & { answers: number[] } = {
      answers: answers.map((value) => value ?? 0),
    };

    (Object.entries(LCADL_SECTIONS) as Array<[LCADLSectionKey, (typeof LCADL_SECTIONS)[LCADLSectionKey]]>).forEach(([key, section]) => {
      const sectionAnswers = answers.slice(index, index + section.activities.length).map((value) => value ?? 0);
      const sectionScore = sectionAnswers.reduce((sum, value) => sum + value, 0);
      const sectionApplicableItems = sectionAnswers.filter((value) => value > 0).length;
      result[key] = {
        activities: [...section.activities],
        answers: sectionAnswers,
        score: sectionScore,
        maxScore: sectionApplicableItems * 5,
      };
      index += section.activities.length;
    });

    onNext({
      answers: result.answers,
      selfCare: result.selfCare!,
      domestic: result.domestic!,
      physical: result.physical!,
      leisure: result.leisure!,
      totalScore,
      maxScore,
      percentage,
      applicableItemsCount,
      denominatorPoints: maxScore,
      rawMaximumScore: totalActivities * 5,
      instrumentVersion: 'LCADL_BR_15_CARPES_2008',
      scoringVersion: 'LCADL_PERCENT_APPLICABLE_ITEMS_V1',
      finishedAt: new Date().toISOString(),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">LCADL - London Chest Activity of Daily Living</p>
          <p className="text-sm opacity-90">
            Versão brasileira de 15 itens. A resposta 0 indica atividade não aplicável e é excluída do denominador percentual.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 pt-4">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            {LCADL_TABS.map((tab, index) => {
              const progress = tabProgress[index];
              const isActive = activeTab === index;
              const isAccessible = index <= activeTab || tabProgress[index - 1]?.complete;

              return (
                <button
                  key={tab.key}
                  type="button"
                  disabled={!isAccessible}
                  onClick={() => isAccessible && setActiveTab(index)}
                  className={`rounded-xl border px-4 py-3 text-left transition ${
                    isActive
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : isAccessible
                        ? 'border-slate-200 bg-slate-50 text-slate-800 hover:border-slate-300 hover:bg-slate-100'
                        : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold">{tab.title}</div>
                    <div className={`rounded-full px-2 py-0.5 text-xs font-bold ${isActive ? 'bg-white/15 text-white' : 'bg-white text-slate-600'}`}>
                      {progress.answered}/{progress.total}
                    </div>
                  </div>
                  <div className={`mt-1 text-xs ${isActive ? 'text-blue-100' : 'text-slate-500'}`}>{tab.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-b border-slate-200 px-4 py-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-lg font-semibold text-slate-900">{currentTab.title}</div>
              <div className="text-sm text-slate-500">{currentTab.description}</div>
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
              Etapa {activeTab + 1} de {LCADL_TABS.length}
            </div>
          </div>
        </div>

        <div className="px-4 py-4">
          <div className="space-y-4">
            {currentSections.map((section) => (
              <div key={section.key} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <h3 className="mb-4 text-base font-semibold text-slate-900">{section.title}</h3>
                <div className="space-y-5">
                  {section.activities.map(({ activity, index }) => (
                    <div key={`${section.key}-${index}`} className="space-y-2.5">
                      <p className="text-sm font-medium text-slate-800">{activity}</p>
                      <div className="space-y-1.5">
                        {LCADL_OPTIONS.map((option) => (
                          <label
                            key={option.value}
                            className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm transition ${
                              answers[index] === option.value
                                ? 'border-blue-600 bg-blue-600 text-white'
                                : 'bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="radio"
                              className="hidden"
                              name={`lcadl_v2_${index}`}
                              checked={answers[index] === option.value}
                              onChange={() => setAnswer(index, option.value)}
                            />
                            <div className="flex-1">{option.label}</div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-slate-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
              Cancelar
            </button>
            <div className="text-sm text-slate-600">
              Pontuação: <span className="font-semibold text-slate-900">{totalScore}</span>/{maxScore}
              <span className="mx-2 text-slate-300">•</span>
              Percentual aplicável: <span className="font-semibold text-slate-900">{percentage.toFixed(1)}%</span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-gray-200 disabled:opacity-50"
              onClick={() => setActiveTab((current) => Math.max(0, current - 1))}
              disabled={activeTab === 0}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Anterior
            </button>

            {activeTab < LCADL_TABS.length - 1 ? (
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => setActiveTab((current) => Math.min(LCADL_TABS.length - 1, current + 1))}
                disabled={!canAdvanceFromCurrentTab}
              >
                Próxima etapa
                <ChevronRight className="ml-1 h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                disabled={!allAnswered}
                onClick={handleFinish}
              >
                Concluir LCADL
                <Check className="ml-1 h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
